import React, { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { useTheme } from '@mui/material/styles';
import { InlineTable } from '../TranslationPanel/InlineTable';
import type { TableSegment } from '../TranslationPanel/translation.types';
import type { OperationNodeSpec, MaterialNodeSpec } from '@/graph/domain/types';
import { sectionHeaderSx, sectionBoxSx, labelValueRowSx, labelSx, valueSx } from './info.styles';
import '../TranslationPanel/translation.styles.css';

interface OperationInfoProps {
  data: OperationNodeSpec & { id: string };
  inputMaterials: (MaterialNodeSpec & { id: string })[];
  outputMaterials: (MaterialNodeSpec & { id: string })[];
}

type ParameterPayload = {
  parameterName?: unknown;
  parameterId?: unknown;
  value?: unknown;
  amount?: unknown;
  unit?: unknown;
  units?: unknown;
};

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null;

const hasAmountUnit = (v: unknown): v is { amount?: unknown; unit?: unknown; units?: unknown } =>
  isRecord(v) && ('amount' in v || 'unit' in v || 'units' in v);

const isPrimitive = (v: unknown): v is string | number | boolean =>
  typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean';

const formatPrimitive = (v: unknown): string => {
  if (v == null) return '—';
  if (isPrimitive(v)) return String(v);
  return '—';
};

const PARAMETER_META_KEYS = new Set(['parameterId', 'parameterName']);

const getAmountUnitText = (v: unknown): string | null => {
  if (!hasAmountUnit(v)) return null;

  const amountRaw = v.amount;
  const unitRaw = v.unit ?? v.units;
  const amount = isPrimitive(amountRaw) ? String(amountRaw) : '';
  const unit = isPrimitive(unitRaw) ? String(unitRaw) : '';
  const merged = `${amount} ${unit}`.trim();

  return (amount === '' && unit === '') ? null : merged;
};

const toCellText = (raw: unknown): string => {
  if (raw == null) return '';
  if (isPrimitive(raw)) return String(raw);

  if (Array.isArray(raw)) {
    return raw.map((item) => toCellText(item)).filter((v) => v !== '').join(', ');
  }

  if (!isRecord(raw)) return String(raw);

  const amountUnit = getAmountUnitText(raw);
  if (amountUnit) return amountUnit;

  if ('value' in raw) return toCellText(raw.value);

  return Object.entries(raw)
    .filter(([k]) => !PARAMETER_META_KEYS.has(k))
    .map(([k, v]) => `${k}: ${toCellText(v)}`)
    .join(', ');
};

const buildTableSegment = (paramName: string, rows: unknown[]): TableSegment | null => {
  const recordRows = rows.filter((r): r is Record<string, unknown> => isRecord(r));
  if (recordRows.length === 0) return null;

  const columnKeys: string[] = [];
  recordRows.forEach((row) => {
    Object.keys(row).forEach((key) => {
      if (!columnKeys.includes(key)) columnKeys.push(key);
    });
  });

  if (columnKeys.length === 0) return null;

  return {
    kind: 'table',
    paramName,
    disabled: true,
    columns: columnKeys.map((key) => ({ key, label: key })),
    rows: recordRows.map((row) => {
      const mappedRow: TableSegment['rows'][number] = {};
      columnKeys.forEach((key) => {
        mappedRow[key] = {
          value: toCellText(row[key]),
          fieldType: 'text',
        };
      });
      return mappedRow;
    }),
  };
};

const renderParameterValue = (
  raw: unknown,
  depth = 0,
  visited = new Set<unknown>(),
  keyPath = 'root'
): React.ReactNode => {
  if (raw == null) return '—';

  if ((isRecord(raw) || Array.isArray(raw)) && visited.has(raw)) {
    return <Typography variant="caption" sx={{ color: 'error.main' }}>[Circular Reference]</Typography>;
  }

  if (isRecord(raw) || Array.isArray(raw)) {
    visited.add(raw);
  }

  if (isPrimitive(raw)) {
    return String(raw);
  }

  if (Array.isArray(raw)) {
    const segment = buildTableSegment('Rows', raw);
    if (segment) {
      return <InlineTable segment={segment} nodeId="info-tab" />;
    }
    return toCellText(raw) || '—';
  }

  if (!isRecord(raw)) {
    return String(raw);
  }

  const amountUnit = getAmountUnitText(raw);
  if (amountUnit) {
    return amountUnit;
  }

  if (Array.isArray(raw.gradientRows)) {
    const gradientSegment = buildTableSegment('Gradient', raw.gradientRows);
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {raw.totalPumps != null && (
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Total Pumps: {formatPrimitive(raw.totalPumps)}
          </Typography>
        )}
        {gradientSegment ? (
          <InlineTable segment={gradientSegment} nodeId="info-tab" />
        ) : (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            No gradient rows
          </Typography>
        )}
      </Box>
    );
  }

  if (Array.isArray(raw.tableRows)) {
    const tableSegment = buildTableSegment('Table', raw.tableRows);
    if (tableSegment) {
      return <InlineTable segment={tableSegment} nodeId="info-tab" />;
    }
    return (
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        No table rows
      </Typography>
    );
  }

  if ('value' in raw && Object.keys(raw).every((k) => PARAMETER_META_KEYS.has(k) || k === 'value')) {
    return renderParameterValue(raw.value, depth, visited, keyPath);
  }

  const entries = Object.entries(raw).filter(([k]) => !PARAMETER_META_KEYS.has(k));
  if (entries.length === 0) return '—';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35 }}>
      {entries.map(([k, v]) => {
        const childKeyPath = `${keyPath}/${k}`;
        return (
          <Box
            key={childKeyPath}
            sx={{
              pl: depth > 0 ? 1.2 : 0,
              ml: depth > 0 ? 0.4 : 0,
              borderLeft: depth > 0 ? '1px solid' : 'none',
              borderColor: 'divider',
            }}
          >
            <Typography variant="caption" sx={{ color: 'text.primary', fontWeight: 600, display: 'block' }}>
              {k}
            </Typography>
            <Box sx={{ ml: 0.5, fontSize: '0.82rem', fontWeight: 500, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {isRecord(v) || Array.isArray(v) ? renderParameterValue(v, depth + 1, visited, childKeyPath) : formatPrimitive(v)}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

const getParameterDisplay = (key: string, rawValue: unknown): { name: string; value: unknown } => {
  if (!isRecord(rawValue)) {
    return { name: key, value: rawValue };
  }

  const parameter = rawValue as ParameterPayload;
  const name = typeof parameter.parameterName === 'string' && parameter.parameterName.trim()
    ? parameter.parameterName
    : key;

  if ('value' in parameter) {
    return { name, value: parameter.value };
  }

  if ('amount' in parameter || 'unit' in parameter || 'units' in parameter) {
    return {
      name,
      value: {
        amount: parameter.amount,
        unit: parameter.unit ?? parameter.units,
      },
    };
  }

  return { name, value: rawValue };
};

const OperationInfoContent: React.FC<OperationInfoProps> = ({
  data,
  inputMaterials,
  outputMaterials,
}) => {
  const theme = useTheme();
  const [error, setError] = useState<string | null>(null);

  const parameters = data?.parameters ?? {};
  const checkPoints = data?.checkPoints ?? [];

  const safeRenderParameterValue = useMemo(
    () => (val: unknown) => {
      try {
        return renderParameterValue(val, 0, new Set(), 'root');
      } catch (err) {
        const errMsg = err instanceof Error ? err.message : 'Unknown error';
        setError(errMsg);
        return <Typography sx={{ color: 'error.main' }}>Error rendering value</Typography>;
      }
    },
    [theme]
  );

  return (
    <Box>
      {error && (
        <Box sx={{ p: 1, mb: 1, bgcolor: 'error.light', borderRadius: 1 }}>
          <Typography variant="caption" sx={{ color: 'error.dark' }}>
            Rendering error: {error}
          </Typography>
        </Box>
      )}

      {/* Header info */}
      <Box sx={sectionBoxSx}>
        <Box sx={labelValueRowSx}>
          <Typography sx={labelSx}>Archetype</Typography>
          <Chip label={data.archetype} size="small" variant="outlined" />
        </Box>
      </Box>

      <Divider />

      {/* Parameters */}
      <Typography sx={sectionHeaderSx}>Parameters</Typography>
      <Box sx={sectionBoxSx}>
        {Object.entries(parameters).map(([key, val]) => {
          const display = getParameterDisplay(key, val);
          return (
            <Box key={key} sx={{ ...labelValueRowSx, alignItems: 'flex-start', gap: 1 }}>
              <Typography sx={labelSx}>{display.name}</Typography>
              <Box sx={{ ...valueSx, textAlign: 'left', fontWeight: 400, whiteSpace: 'normal' }}>
                {safeRenderParameterValue(display.value)}
              </Box>
            </Box>
          );
        })}
        {Object.keys(parameters).length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No parameters configured.
          </Typography>
        )}
      </Box>

      <Divider />

      {/* Checkpoints */}
      {checkPoints.length > 0 && (
        <>
          <Typography sx={sectionHeaderSx}>
            Checkpoints ({checkPoints.length})
          </Typography>
          <Box sx={sectionBoxSx}>
            {checkPoints.map((cp) => (
              <Box key={cp.id} sx={{ py: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CheckBoxOutlineBlankIcon sx={{ fontSize: '1.1rem' }} />
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {cp.checkpointName} ({cp.fieldType})
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                  Mandatory: {cp.required ? 'Yes' : 'No'}
                  {cp.rangeMin != null && cp.rangeMax != null
                    ? ` · Acceptance: ${cp.rangeMin}–${cp.rangeMax}`
                    : ''}
                </Typography>
              </Box>
            ))}
          </Box>
          <Divider />
        </>
      )}

      {/* Input Materials */}
      <Typography sx={sectionHeaderSx}>Input Materials</Typography>
      <Box sx={sectionBoxSx}>
        {inputMaterials.length > 0 ? (
          inputMaterials.map((mat) => (
            <Typography key={mat.id} variant="body2" sx={{ py: 0.25 }}>
              → {mat.name} ({mat.container.name})
            </Typography>
          ))
        ) : (
          <Typography variant="body2" color="text.secondary">
            None
          </Typography>
        )}
      </Box>

      <Divider />

      {/* Output Materials */}
      <Typography sx={sectionHeaderSx}>Output Materials</Typography>
      <Box sx={sectionBoxSx}>
        {outputMaterials.length > 0 ? (
          outputMaterials.map((mat) => (
            <Typography key={mat.id} variant="body2" sx={{ py: 0.25 }}>
              → {mat.name} ({mat.container.name})
            </Typography>
          ))
        ) : (
          <Typography variant="body2" color="text.secondary">
            None
          </Typography>
        )}
      </Box>

      {/* Notes */}
      {data.notes && (
        <>
          <Divider />
          <Typography sx={sectionHeaderSx}>Notes</Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {data.notes}
          </Typography>
        </>
      )}
    </Box>
  );
};

export const OperationInfo: React.FC<OperationInfoProps> = React.memo(OperationInfoContent);
